import { scopeAddress, type CoreScope } from '../../core/scope'
import { useLayoutEffect, useRef, type HTMLAttributes, type ReactNode } from 'react'
import type { Blocks } from '@w/content-blocks'
import type { CoreViewApi } from '../view-api'

const placeholderClass = 'data-[cb-field-placeholder]:after:pointer-events-none data-[cb-field-placeholder]:after:text-on-surface-secondary data-[cb-field-placeholder]:after:content-[attr(data-cb-field-placeholder)]'

/** Scalar array fields have positional schema slots, unlike block lists with CRDT item IDs. */
export function fieldEntries<T>(values: readonly T[], owner: string) {
  return values.map((value, index) => ({ key: `${owner}:${index}`, value, index }))
}

/** A prop-backed text field. Core's ordinary row editor does not own this DOM island. */
export function TextField({ value, onChange, api, as: Tag = 'span', children, onFieldInput, singleLine, ...props }: {
  value: string
  onChange: (value: string) => void
  api: CoreViewApi
  as?: 'span' | 'div' | 'h1' | 'figcaption'
  children?: ReactNode
  onFieldInput?: (type: string) => boolean
  /** One plain line: pasted line breaks become single spaces; Enter commits and blurs (wbd-focus-field-done). */
  singleLine?: boolean
} & Omit<HTMLAttributes<HTMLElement>, 'onChange' | 'children'>) {
  const { ref, commit } = useFieldDOM(api, element => { onChange(fieldText(element)) }, onFieldInput, singleLine)
  return <Tag tabIndex={api.mode === 'edit' ? 0 : -1} {...props} ref={element => { ref.current = element }} data-core-field="true" contentEditable={api.mode === 'edit'} suppressContentEditableWarning
    className={`${props.className ?? ''} ${placeholderClass}`}
    onFocus={() => { api.select(null) }}
    onBeforeInput={event => { event.stopPropagation() }}
    onKeyDown={event => { event.stopPropagation(); props.onKeyDown?.(event) }}
    onInput={event => { event.stopPropagation(); if (!event.nativeEvent.isComposing) commit() }}
    onCompositionEnd={() => { commit() }}
    >
    {value}{children}
  </Tag>
}

/** Rich fields are DOM containers for the host's core pipeline. */
export function RichField({ value, scope, api, as: Tag = 'div', children, onFieldInput, ...props }: {
  value: Blocks
  scope: CoreScope
  api: CoreViewApi
  as?: 'div' | 'span' | 'figcaption'
  children?: ReactNode
  onFieldInput?: (type: string) => boolean
} & Omit<HTMLAttributes<HTMLElement>, 'onChange' | 'children'>) {
  const { renderBlock } = api
  const ref = useRef<HTMLElement | null>(null)
  useLayoutEffect(() => {
    const element = ref.current
    if (!element || !onFieldInput) return () => {}
    const input = (event: InputEvent) => {
      if (onFieldInput(event.inputType)) { event.preventDefault(); event.stopPropagation() }
    }
    const key = (event: KeyboardEvent) => {
      if (event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) return
      if (onFieldInput(event.key === 'Enter' ? 'insertParagraph' : event.key === 'Backspace' ? 'deleteContentBackward' : '')) {
        event.preventDefault(); event.stopPropagation()
      }
    }
    element.addEventListener('beforeinput', input)
    element.addEventListener('keydown', key)
    return () => { element.removeEventListener('beforeinput', input); element.removeEventListener('keydown', key) }
  }, [onFieldInput])
  return <Tag ref={element => { ref.current = element }} role="textbox" tabIndex={api.mode === 'edit' ? 0 : -1} aria-multiline="true" {...props}
    data-core-scope={scopeAddress(scope)} contentEditable={api.mode === 'edit'} suppressContentEditableWarning
    className={`${props.className ?? ''} ${placeholderClass}`}>
    {value.map(block => renderBlock(block))}{children}
  </Tag>
}

type OwnedDOM = { node: Node; text: string | null; children: OwnedDOM[] }
type FieldSelection = { anchor: number; focus: number }

function fieldText(node: Node): string {
  if (node instanceof HTMLBRElement) return '\n'
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? ''
  return Array.from(node.childNodes, child => fieldText(child)).join('')
}

function captureDOM(node: Node): OwnedDOM {
  return { node, text: node.nodeValue, children: Array.from(node.childNodes, child => captureDOM(child)) }
}

function restoreDOM(tree: OwnedDOM) {
  if (tree.node.nodeType === Node.TEXT_NODE) tree.node.nodeValue = tree.text
  else {
    for (const child of tree.children) restoreDOM(child)
    if (tree.node instanceof Element) tree.node.replaceChildren(...tree.children.map(child => child.node))
  }
}

function fieldSelection(element: HTMLElement): FieldSelection | null {
  const selection = window.getSelection()
  if (!selection?.anchorNode || !selection.focusNode || !element.contains(selection.anchorNode) || !element.contains(selection.focusNode)) return null
  const offset = (node: Node, index: number) => {
    const range = document.createRange()
    range.selectNodeContents(element)
    range.setEnd(node, index)
    return fieldText(range.cloneContents()).length
  }
  return { anchor: offset(selection.anchorNode, selection.anchorOffset), focus: offset(selection.focusNode, selection.focusOffset) }
}

function fieldPoint(element: HTMLElement, offset: number): { node: Node; offset: number } {
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, { acceptNode: node => node.nodeType === Node.TEXT_NODE || node instanceof HTMLBRElement ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP })
  let node = walker.nextNode()
  let last: Node = element
  while (node) {
    last = node
    if (node instanceof HTMLBRElement && node.parentNode) {
      if (offset <= 1) return { node: node.parentNode, offset: Array.prototype.indexOf.call(node.parentNode.childNodes, node) + offset }
      offset -= 1; node = walker.nextNode(); continue
    }
    if (offset <= (node.textContent?.length ?? 0)) return { node, offset }
    offset -= node.textContent?.length ?? 0
    node = walker.nextNode()
  }
  return { node: last, offset: last === element ? 0 : last.textContent?.length ?? 0 }
}

/** Restore React's owned nodes before reconciliation; native edits may remove whole rich-text spans. */
function useFieldDOM(api: CoreViewApi, onCommit: (element: HTMLElement) => void, onFieldInput: ((type: string) => boolean) | undefined, singleLine = false) {
  const ref = useRef<HTMLElement | null>(null)
  const owned = useRef<OwnedDOM | null>(null)
  const pendingSelection = useRef<FieldSelection | null>(null)
  function commit() {
    const element = ref.current
    if (!element || api.mode !== 'edit') return
    const changed = element.cloneNode(true) as HTMLElement
    pendingSelection.current = fieldSelection(element)
    if (owned.current) restoreDOM(owned.current)
    onCommit(changed)
  }
  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return () => {}
    if (pendingSelection.current) {
      const anchor = fieldPoint(element, pendingSelection.current.anchor)
      const focus = fieldPoint(element, pendingSelection.current.focus)
      window.getSelection()?.setBaseAndExtent(anchor.node, anchor.offset, focus.node, focus.offset)
      pendingSelection.current = null
    }
    owned.current = captureDOM(element)
    const beforeInput = (event: InputEvent) => {
      if (api.mode !== 'edit' || event.isComposing || event.inputType === 'insertCompositionText') return
      if (event.inputType === 'historyUndo' || event.inputType === 'historyRedo') {
        event.preventDefault(); event.stopPropagation(); api.dispatch({ type: event.inputType }); return
      }
      if (onFieldInput?.(event.inputType)) { event.preventDefault(); event.stopPropagation(); return }
      // wbd-focus-field-done: Enter in a one-line string field commits and closes it; no newline is inserted.
      if (singleLine && (event.inputType === 'insertParagraph' || event.inputType === 'insertLineBreak')) {
        event.preventDefault(); event.stopPropagation(); element.blur(); return
      }
      if (!['insertText', 'insertParagraph', 'insertLineBreak', 'deleteContentBackward', 'deleteContentForward'].includes(event.inputType)) return
      event.preventDefault(); event.stopPropagation()
      const text = fieldText(element)
      const selected = fieldSelection(element) ?? { anchor: text.length, focus: text.length }
      let start = Math.min(selected.anchor, selected.focus)
      let end = Math.max(selected.anchor, selected.focus)
      if (start === end && event.inputType.startsWith('delete')) {
        const boundaries = Array.from(new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(text), item => item.index)
        if (event.inputType === 'deleteContentBackward') start = boundaries.findLast(index => index < start) ?? 0
        else end = boundaries.find(index => index > end) ?? text.length
      }
      const from = fieldPoint(element, start)
      const to = fieldPoint(element, end)
      const native = window.getSelection()
      const preserveBoundary = start === end && !event.inputType.startsWith('delete') && fieldSelection(element) && native?.rangeCount
      const range = preserveBoundary ? native.getRangeAt(0).cloneRange() : document.createRange()
      if (!preserveBoundary) {
        range.setStart(from.node, from.offset); range.setEnd(to.node, to.offset)
      }
      range.deleteContents()
      const inserted = event.inputType.startsWith('delete') ? '' : event.inputType === 'insertText' ? event.data ?? '' : '\n'
      if (inserted) {
        const leaf = document.createTextNode(inserted)
        range.insertNode(leaf); range.setStartAfter(leaf)
      }
      range.collapse(true)
      window.getSelection()?.removeAllRanges(); window.getSelection()?.addRange(range)
      commit()
    }
    const keyDown = (event: KeyboardEvent) => {
      if (api.mode !== 'edit' || !event.metaKey && !event.ctrlKey) return
      if (event.key.toLowerCase() === 'z') {
        event.preventDefault(); event.stopPropagation(); api.dispatch({ type: event.shiftKey ? 'historyRedo' : 'historyUndo' })
      } else if (event.key.toLowerCase() === 'a' && !event.altKey && !event.shiftKey) {
        // Nested editable fields can keep the outer root focused. Its relayed key has no native default action.
        event.preventDefault(); event.stopPropagation()
        const range = document.createRange()
        range.selectNodeContents(element)
        window.getSelection()?.removeAllRanges(); window.getSelection()?.addRange(range)
      }

    }
    const paste = (event: ClipboardEvent) => {
      if (api.mode !== 'edit' || !event.clipboardData) return
      event.preventDefault(); event.stopPropagation()
      const selected = fieldSelection(element)
      const selection = window.getSelection()
      const range = selected && selection?.rangeCount ? selection.getRangeAt(0) : document.createRange()
      if (!selected) { range.selectNodeContents(element); range.collapse(false) }
      const fragment = document.createDocumentFragment()
      const pasted = event.clipboardData.getData('text/plain')
      fragment.append(document.createTextNode(singleLine ? pasted.replaceAll(/\s*[\r\n\u2028\u2029]+\s*/gu, ' ') : pasted))
      const last = fragment.lastChild
      range.deleteContents(); range.insertNode(fragment)
      if (last) range.setStartAfter(last)
      range.collapse(true); selection?.removeAllRanges(); selection?.addRange(range)
      commit()
    }
    const copy = (event: ClipboardEvent) => {
      const selected = fieldSelection(element)
      const selection = window.getSelection()
      if (!selected || !selection?.rangeCount || !event.clipboardData) return
      event.preventDefault(); event.stopPropagation()
      const range = selection.getRangeAt(0)
      const content = document.createElement('div'); content.append(range.cloneContents())
      event.clipboardData.setData('text/plain', fieldText(content))
      if (event.type === 'cut' && api.mode === 'edit') { range.deleteContents(); commit() }
    }
    element.addEventListener('beforeinput', beforeInput)
    element.addEventListener('paste', paste)
    element.addEventListener('keydown', keyDown)
    element.addEventListener('copy', copy)
    element.addEventListener('cut', copy)
    return () => {
      element.removeEventListener('beforeinput', beforeInput); element.removeEventListener('paste', paste); element.removeEventListener('keydown', keyDown)
      element.removeEventListener('copy', copy); element.removeEventListener('cut', copy)
    }
  })
  return { ref, commit }
}
